import React, { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import { governancePipelineSteps } from "./data";

const GovernancePipeline = () => {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 md:p-12 font-sans text-slate-200">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <h2 className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-rose-400 via-purple-400 to-blue-400">
          Governance-First Architecture
        </h2>
        <p className="text-slate-500 mt-2 text-sm md:text-base">
          Protocols define the workflow before the user acts.
        </p>
      </div>

      {/* Responsive Container */}
      <div className="relative w-full max-w-6xl">
        {/* DESKTOP CONNECTING LINE (Horizontal) */}
        <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-rose-900 via-purple-900 to-emerald-900 -translate-y-1/2 z-0 rounded-full opacity-50"></div>

        {/* MOBILE CONNECTING LINE (Vertical) */}
        <div className="md:hidden absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-rose-900 via-purple-900 to-emerald-900 z-0 rounded-full opacity-50"></div>

        {/* --- GRID SYSTEM --- */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-4 relative z-10">
          {governancePipelineSteps.map((step, index) => (
            <div
              key={step.id}
              className={`relative flex md:flex-col items-center group cursor-pointer`}
              onClick={() =>
                setActiveStep(activeStep === step.id ? null : step.id)
              }
            >
              {/* 1. THE NODE (Circle) */}
              <div
                className={`
                relative w-16 h-16 shrink-0 rounded-full bg-slate-900 border-2 
                flex items-center justify-center transition-all duration-500 z-20
                ${
                  activeStep === step.id
                    ? `${step.borderColor} scale-110 ${step.bgGlow}`
                    : "border-slate-700 hover:border-slate-500"
                }
              `}
              >
                <div
                  className={`${step.color} transition-transform duration-500 ${
                    activeStep === step.id ? "scale-110" : ""
                  }`}
                >
                  {step.icon}
                </div>

                {/* Pulsing Ring for active state */}
                {activeStep === step.id && (
                  <div
                    className={`absolute inset-0 rounded-full border ${step.borderColor} animate-ping opacity-20`}
                  ></div>
                )}
              </div>

              {/* 2. LABEL (Side on Mobile, Bottom on Desktop) */}
              <div className="ml-6 md:ml-0 md:mt-6 text-left md:text-center transition-all duration-300">
                <h3
                  className={`font-bold text-sm md:text-base uppercase tracking-wider ${
                    activeStep === step.id ? step.color : "text-slate-400"
                  }`}
                >
                  {step.title}
                </h3>
                <p className="text-[10px] md:text-xs text-slate-500 mt-1 hidden md:block">
                  Step 0{index + 1}
                </p>
                {/* Mobile 'Click for info' hint */}
                <p className="md:hidden text-[10px] text-slate-600 mt-1">
                  Tap for details
                </p>
              </div>

              {/* 3. MOBILE: Inline Connector Arrow (Visual only) */}
              <div className="md:hidden absolute left-[31px] top-16 w-0.5 h-12 bg-slate-800 -z-10"></div>
            </div>
          ))}
        </div>
      </div>

      {/* --- INFO POPUP / OVERLAY --- */}
      {/* This renders centrally on mobile (modal) and floating on desktop */}
      <div
        className={`
        fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm transition-all duration-300
        ${
          activeStep
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }
      `}
      >
        {activeStep &&
          (() => {
            const current = governancePipelineSteps.find(
              (s) => s.id === activeStep
            );
            return (
              <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 scale-100">
                {/* Card Header */}
                <div
                  className={`p-6 border-b border-slate-800 flex justify-between items-start bg-gradient-to-r from-slate-900 to-slate-800`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg bg-slate-950 border ${current.borderColor}`}
                    >
                      {React.cloneElement(current.icon, {
                        size: 20,
                        className: current.color,
                      })}
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${current.color}`}>
                        {current.title}
                      </h3>
                      <span className="text-xs text-slate-500 uppercase tracking-widest">
                        Active State
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveStep(null)}
                    className="p-1 rounded-full hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                  >
                    <IoCloseSharp size={20} />
                  </button>
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-4">
                  <p className="text-slate-300 leading-relaxed text-sm">
                    {current.description}
                  </p>

                  {/* Animated List of Details */}
                  <div className="space-y-2 mt-4">
                    <p className="text-xs font-bold text-slate-500 uppercase">
                      Context Loaded:
                    </p>
                    {current.details.map((detail, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 p-3 rounded bg-slate-950 border border-slate-800 animate-[fadeIn_0.5s_ease-out_forwards]"
                        style={{ animationDelay: `${i * 100}ms` }}
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${current.color.replace(
                            "text-",
                            "bg-"
                          )}`}
                        ></div>
                        <span className="text-xs md:text-sm text-slate-300">
                          {detail}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Footer */}
                <div className="p-4 bg-slate-950 border-t border-slate-800 text-center">
                  <button
                    onClick={() => setActiveStep(null)}
                    className="text-xs text-slate-500 hover:text-white transition-colors flex items-center justify-center gap-1 w-full"
                  >
                    Close Detail View <FaChevronDown size={12} />
                  </button>
                </div>
              </div>
            );
          })()}
      </div>

      {/* Global Style for Keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default GovernancePipeline;
